/**
 * A aritmética dos relatórios financeiros, num lugar só.
 *
 * Os dois adaptadores — memória e PostgREST — leem as mesmas linhas de título e
 * de pagamento e precisam chegar ao mesmo número. Escrever a conta duas vezes é
 * garantir que uma delas divirja no primeiro ajuste, e divergência em relatório
 * financeiro só aparece quando alguém compara com o extrato do banco.
 */

import type { LinhaDeCompetencia, LinhaDeFluxo, Movimentacao, Titulo, TotalDaCategoria } from './tipos';

/** Os `quantos` meses que terminam em `mesFinal` (`YYYY-MM`), do mais antigo ao mais recente. */
export function janelaDeMeses(mesFinal: string, quantos: number): string[] {
    const [ano, mes] = mesFinal.split('-').map(Number);
    if (!ano || !mes) return [];
    const meses: string[] = [];
    for (let i = quantos - 1; i >= 0; i -= 1) {
        /* `Date` com dia 1 e mês fora da faixa normaliza sozinho: mês 0 vira
           dezembro do ano anterior, sem conta de virada de ano à mão. */
        const d = new Date(Date.UTC(ano, mes - 1 - i, 1));
        meses.push(`${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`);
    }
    return meses;
}

/** Os dias do mês `YYYY-MM`, em ordem. */
export function diasDoMes(mes: string): string[] {
    const [ano, m] = mes.split('-').map(Number);
    if (!ano || !m) return [];
    /* Dia 0 do mês seguinte é o último deste — evita a tabela de 30/31 e o ano
       bissexto. */
    const ultimo = new Date(Date.UTC(ano, m, 0)).getUTCDate();
    return Array.from({ length: ultimo }, (_, i) => `${mes}-${String(i + 1).padStart(2, '0')}`);
}

/**
 * Competência x caixa, mês a mês.
 *
 * Receita e despesa saem do título, pelo mês a que ele se REFERE; o caixa sai
 * do pagamento, pelo dia em que o dinheiro andou. Título cancelado fica fora
 * das duas: obrigação desfeita não é resultado.
 */
export function relatorioDeCompetencia(titulos: Titulo[], movimentacoes: Movimentacao[], meses: string[]): LinhaDeCompetencia[] {
    return meses.map((mes) => {
        const doMes = titulos.filter((t) => t.competencia.slice(0, 7) === mes && t.situacao !== 'cancelado');
        const pagos = movimentacoes.filter((m) => m.pagoEm.slice(0, 7) === mes);
        return {
            mes,
            receitaCentavos: doMes.filter((t) => t.tipo === 'receber').reduce((s, t) => s + t.valorCentavos, 0),
            despesaCentavos: doMes.filter((t) => t.tipo === 'pagar').reduce((s, t) => s + t.valorCentavos, 0),
            /* Entra na conta o líquido, não o bruto: a taxa do cartão nunca
               chegou a ser dinheiro da clínica. */
            caixaEntradasCentavos: pagos.filter((m) => m.tipo === 'receber').reduce((s, m) => s + m.liquidoCentavos, 0),
            caixaSaidasCentavos: pagos.filter((m) => m.tipo === 'pagar').reduce((s, m) => s + m.valorCentavos, 0),
        };
    });
}

/**
 * Fluxo dia a dia do mês.
 *
 * `saldoCentavos` é o acumulado DO PERÍODO, não o saldo das contas: somar o
 * saldo inicial de cada conta aqui faria a primeira linha do mês parecer
 * movimento do dia.
 */
export function fluxoDiario(movimentacoes: Movimentacao[], mes: string): LinhaDeFluxo[] {
    let acumulado = 0;
    return diasDoMes(mes).map((dia) => {
        const doDia = movimentacoes.filter((m) => m.pagoEm === dia);
        const entradas = doDia.filter((m) => m.tipo === 'receber').reduce((s, m) => s + m.liquidoCentavos, 0);
        const saidas = doDia.filter((m) => m.tipo === 'pagar').reduce((s, m) => s + m.valorCentavos, 0);
        acumulado += entradas - saidas;
        return { chave: dia, entradasCentavos: entradas, saidasCentavos: saidas, saldoCentavos: acumulado };
    });
}

/** Fluxo mês a mês. Aqui `saldoCentavos` é o resultado do próprio mês. */
export function fluxoMensal(movimentacoes: Movimentacao[], meses: string[]): LinhaDeFluxo[] {
    return meses.map((mes) => {
        const doMes = movimentacoes.filter((m) => m.pagoEm.slice(0, 7) === mes);
        const entradas = doMes.filter((m) => m.tipo === 'receber').reduce((s, m) => s + m.liquidoCentavos, 0);
        const saidas = doMes.filter((m) => m.tipo === 'pagar').reduce((s, m) => s + m.valorCentavos, 0);
        return { chave: mes, entradasCentavos: entradas, saidasCentavos: saidas, saldoCentavos: entradas - saidas };
    });
}

/**
 * Quanto cada categoria somou na competência de `mes` (`YYYY-MM`).
 *
 * Pelo id, não pelo nome: "Outros" de receita e "Outros" de despesa são duas
 * categorias. A categoria-pai não soma as filhas aqui — quem mostra decide se
 * agrupa. Título cancelado fica fora, como no relatório de competência.
 */
export function totaisPorCategoria(titulos: Titulo[], mes: string): TotalDaCategoria[] {
    const soma = new Map<string, TotalDaCategoria>();
    for (const t of titulos) {
        if (!t.categoriaId || t.situacao === 'cancelado' || t.competencia.slice(0, 7) !== mes) continue;
        const atual = soma.get(t.categoriaId) ?? { categoriaId: t.categoriaId, totalCentavos: 0, lancamentos: 0 };
        atual.totalCentavos += t.valorCentavos;
        atual.lancamentos += 1;
        soma.set(t.categoriaId, atual);
    }
    return [...soma.values()];
}
